<template>
  <div class="food-label-manage">
    <a-config-provider :locale="zhCN">
      <a-card :bordered="false">
        <template #title>
          <div style="display: flex; align-items: center; gap: 12px">
            <a-button type="text" style="padding: 0" @click="goBack">
              <template #icon><ArrowLeftOutlined /></template>
            </a-button>
            <span style="font-size: 18px; font-weight: 600">菜品标签管理</span>
          </div>
        </template>

        <!-- 查询表单 -->
        <a-form
          :model="queryParams"
          layout="inline"
          class="search-form"
          @submit="handleSearch"
          @submit.prevent
        >
          <a-form-item label="店铺">
            <a-select
              v-model:value="queryParams.data.shopId"
              disabled
              placeholder="请选择店铺"
              style="width: 200px"
              @change="cacheSelectedShop"
            >
              <a-select-option v-for="shop in shopList" :key="shop.shopId" :value="shop.shopId">
                {{ shop.shopName }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="标签名称">
            <a-input
              v-model:value="queryParams.data.labelName"
              placeholder="请输入标签名称"
              allow-clear
              style="width: 200px"
            />
          </a-form-item>

          <a-form-item label="标签类型">
            <a-select
              v-model:value="queryParams.data.tagType"
              placeholder="请选择标签类型"
              allow-clear
              style="width: 140px"
            >
              <a-select-option
                v-for="option in tagTypeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="状态">
            <a-select
              v-model:value="queryParams.data.labelStatus"
              placeholder="请选择状态"
              allow-clear
              style="width: 120px"
            >
              <a-select-option :value="0">正常</a-select-option>
              <a-select-option :value="1">禁用</a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="创建时间">
            <a-range-picker
              v-model:value="dateRange"
              format="YYYY-MM-DD"
              style="width: 240px"
              @change="handleDateRangeChange"
            />
          </a-form-item>

          <a-form-item>
            <a-button type="primary" html-type="submit" :loading="loading">
              <template #icon><SearchOutlined /></template>
              查询
            </a-button>
            <a-button style="margin-left: 8px" @click="handleReset">
              <template #icon><ReloadOutlined /></template>
              重置
            </a-button>
          </a-form-item>
        </a-form>

        <!-- 顶部操作 -->
        <div style="margin-bottom: 16px; text-align: right">
          <a-button type="primary" @click="openAddLabelModal"> 新增标签 </a-button>
        </div>

        <!-- 数据表格 -->
        <a-table
          :columns="columns"
          :data-source="tableData"
          :loading="loading"
          :pagination="false"
          row-key="shopFoodLabelId"
          :scroll="{ x: 'max-content' }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'labelStatus'">
              <a-tag :color="record.labelStatus === 0 ? 'green' : 'red'">
                {{ record.labelStatus === 0 ? '正常' : '禁用' }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'tagType'">
              <span>{{ getTagTypeLabel(record.tagType) }}</span>
            </template>
            <template v-else-if="column.key === 'shopName'">
              <span>{{ getShopName(record.shopId) }}</span>
            </template>
            <template v-else-if="column.key === 'createTime'">
              <span>{{ formatDateTime(record.createTime) }}</span>
            </template>
            <template v-else-if="column.key === 'actions'">
              <a-space>
                <a-button type="link" size="small" @click="handleEditLabel(record)">
                  编辑
                </a-button>
                <a-popconfirm
                  :title="record.labelStatus === 0 ? '确认禁用该标签？' : '确认启用该标签？'"
                  ok-text="是"
                  cancel-text="否"
                  @confirm="handleToggleLabelStatus(record)"
                >
                  <a-button type="link" size="small">
                    {{ record.labelStatus === 0 ? '禁用' : '启用' }}
                  </a-button>
                </a-popconfirm>
                <a-popconfirm
                  title="确认删除该标签？"
                  ok-text="是"
                  cancel-text="否"
                  @confirm="handleDeleteLabel(record)"
                >
                  <a-button type="link" danger size="small">删除</a-button>
                </a-popconfirm>
              </a-space>
            </template>
          </template>
        </a-table>

        <!-- 分页 -->
        <div class="pagination-container">
          <a-pagination
            v-model:current="queryParams.pageNum"
            v-model:page-size="queryParams.pageSize"
            :total="total"
            :show-total="(total) => `共 ${total} 条`"
            :show-size-changer="true"
            :page-size-options="['10', '20', '50', '100']"
            @change="handlePageChange"
            @show-size-change="handlePageSizeChange"
          />
        </div>
      </a-card>
    </a-config-provider>
  </div>

  <!-- 新增 / 编辑标签弹窗 -->
  <a-modal
    v-model:open="labelModalVisible"
    :title="labelModalTitle"
    :confirm-loading="labelModalLoading"
    ok-text="保存"
    cancel-text="取消"
    :width="640"
    wrap-class-name="label-modal"
    @ok="handleLabelModalOk"
    @cancel="handleLabelModalCancel"
  >
    <a-form layout="vertical">
      <a-form-item label="店铺" required>
        <!-- 新增时选择店铺，编辑时仅展示店铺名称 -->
        <a-select v-if="!isEdit" v-model:value="labelForm.shopId" disabled placeholder="请选择店铺">
          <a-select-option v-for="shop in shopList" :key="shop.shopId" :value="shop.shopId">
            {{ shop.shopName }}
          </a-select-option>
        </a-select>
        <a-input v-else :value="getShopName(labelForm.shopId)" disabled placeholder="店铺" />
      </a-form-item>
      <a-form-item label="标签名称" required>
        <a-input v-model:value="labelForm.labelName" placeholder="请输入标签名称" />
      </a-form-item>
      <a-form-item label="标签类型" required>
        <a-select v-model:value="labelForm.tagType" placeholder="请选择标签类型" allow-clear>
          <a-select-option
            v-for="option in tagTypeOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="翻译语言">
        <a-select
          v-model:value="labelForm.translationLanguage"
          placeholder="请选择翻译语言"
          @change="handleLabelTranslationLanguageChange"
        >
          <a-select-option
            v-for="language in translationLanguageOptions"
            :key="language.value"
            :value="language.value"
          >
            {{ language.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item label="标签翻译名称">
        <a-input v-model:value="labelForm.translationTagName" placeholder="请输入标签翻译名称" />
      </a-form-item>
      <a-form-item label="排序">
        <a-input-number
          v-model:value="labelForm.sort"
          :min="0"
          style="width: 100%"
          placeholder="请输入排序（数字，越小越靠前）"
        />
      </a-form-item>
      <a-form-item label="备注">
        <a-textarea
          v-model:value="labelForm.remark"
          :rows="3"
          :maxlength="200"
          show-count
          placeholder="请输入备注"
        />
      </a-form-item>
    </a-form>
  </a-modal>

  <!-- 标签详情弹窗 -->
  <a-modal v-model:open="labelDetailVisible" title="标签详情" :footer="null" :width="520">
    <a-spin :spinning="labelDetailLoading">
      <a-descriptions v-if="labelDetail" bordered column="1" size="small">
        <a-descriptions-item label="标签ID">
          {{ labelDetail.shopFoodTagId }}
        </a-descriptions-item>
        <a-descriptions-item label="标签名称">
          {{ labelDetail.tagName }}
        </a-descriptions-item>
        <a-descriptions-item label="标签类型">
          {{ getTagTypeLabel(labelDetail.tagType) }}
        </a-descriptions-item>
        <a-descriptions-item label="状态">
          <a-tag :color="labelDetail.tagStatus === 0 ? 'green' : 'red'">
            {{ labelDetail.tagStatus === 0 ? '正常' : '禁用' }}
          </a-tag>
        </a-descriptions-item>
        <a-descriptions-item label="排序">
          {{ labelDetail.sort }}
        </a-descriptions-item>
        <a-descriptions-item label="备注">
          {{ labelDetail.remark || '-' }}
        </a-descriptions-item>
        <a-descriptions-item label="创建时间">
          {{ formatDateTime(labelDetail.createTime) }}
        </a-descriptions-item>
        <a-descriptions-item label="修改时间">
          {{ formatDateTime(labelDetail.modifyTime) }}
        </a-descriptions-item>
      </a-descriptions>
      <div v-else style="text-align: center; padding: 16px 0">暂无数据</div>
    </a-spin>
  </a-modal>
</template>

<script setup lang="ts">
  import { ref, reactive, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { SearchOutlined, ReloadOutlined, ArrowLeftOutlined } from '@ant-design/icons-vue';
  import { message } from 'ant-design-vue';
  import zhCN from 'ant-design-vue/es/locale/zh_CN';
  import dayjs, { type Dayjs } from 'dayjs';
  import { restoreSelectedShop, cacheSelectedShop } from '@/utils/selectedShop';
  import {
    fetchTagList,
    fetchTagDetail,
    addTag,
    updateTag,
    deleteTag,
    forbidTag,
    insertLanguageTranslation,
    updateLanguageTranslation,
    fetchLanguageTranslationList,
  } from '@/service/food';
  import { fetchShopList } from '@/service/base';
  import type { FoodTagDetailModel } from '@/model/food';

  type TagType = 1 | 2;

  const tagTypeOptions: Array<{ label: string; value: TagType }> = [
    { label: '分类标签', value: 1 },
    { label: '商品标签', value: 2 },
  ];

  // 表格列定义
  const columns = [
    {
      title: '标签ID',
      dataIndex: 'shopFoodLabelId',
      key: 'shopFoodLabelId',
      width: 100,
    },
    {
      title: '标签名称',
      dataIndex: 'labelName',
      key: 'labelName',
      width: 150,
    },
    {
      title: '标签类型',
      dataIndex: 'tagType',
      key: 'tagType',
      width: 120,
    },
    {
      title: '状态',
      dataIndex: 'labelStatus',
      key: 'labelStatus',
      width: 100,
    },
    {
      title: '店铺名称',
      dataIndex: 'shopName',
      key: 'shopName',
      width: 150,
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
    },
    {
      title: '操作',
      key: 'actions',
      width: 260,
      fixed: 'right',
    },
  ];

  // 响应式数据
  const loading = ref(false);
  const shopList = ref<any[]>([]);
  const tableData = ref<any[]>([]);
  const total = ref(0);
  const dateRange = ref<[Dayjs, Dayjs] | null>(null);
  const translationLanguageOptions = [
    { label: '英语', value: 'en' },
    { label: '法语', value: 'ja' },
  ];

  // 新增 / 编辑标签弹窗
  const labelModalVisible = ref(false);
  const labelModalLoading = ref(false);
  const isEdit = ref(false);
  const labelModalTitle = ref('新增标签');
  const labelForm = reactive({
    shopFoodLabelId: undefined as number | undefined,
    shopId: undefined as number | undefined,
    labelName: '',
    tagType: undefined as TagType | undefined,
    labelStatus: 0 as 0 | 1,
    sort: 1 as number,
    remark: '',
    translationLanguage: 'en',
    languageTranslationId: undefined as number | undefined,
    translationTagName: '',
  });

  // 标签详情弹窗
  const labelDetailVisible = ref(false);
  const labelDetailLoading = ref(false);
  const labelDetail = ref<FoodTagDetailModel | null>(null);

  // 查询参数
  const queryParams = reactive({
    createtimeBegin: '',
    createtimeEnd: '',
    pageNum: 1,
    pageSize: 20,
    data: {
      shopId: undefined as number | undefined,
      labelName: '',
      tagType: undefined as TagType | undefined,
      labelStatus: undefined as number | undefined,
    },
  });

  // 格式化日期时间
  const formatDateTime = (dateStr: string | Date | undefined) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('YYYY-MM-DD HH:mm:ss');
  };

  const getTagTypeLabel = (tagType?: number) =>
    tagTypeOptions.find((option) => option.value === tagType)?.label || '-';

  // 根据店铺ID获取店铺名称（用于列表展示）
  const getShopName = (shopId?: number | string) => {
    if (!shopId) return '-';
    const idNum = Number(shopId);
    const shop = shopList.value.find((s) => s.shopId === idNum);
    return shop?.shopName || '-';
  };

  const getSelectedShop = (shopId?: number) => {
    if (!shopId) return undefined;
    return shopList.value.find((shop) => Number(shop.shopId) === Number(shopId));
  };

  const submitTagTranslation = async (tagId?: number) => {
    const translationTagName = labelForm.translationTagName.trim();
    if (!translationTagName) return;
    if (!tagId || !labelForm.shopId) {
      message.warning('标签保存成功，翻译缺少标签ID或店铺信息，未保存');
      return;
    }

    const shop = getSelectedShop(labelForm.shopId);
    if (!shop) {
      message.warning('标签保存成功，翻译缺少店铺公司信息，未保存');
      return;
    }

    const translationJson = JSON.stringify({ tagName: translationTagName });
    const resp = labelForm.languageTranslationId
      ? await updateLanguageTranslation({
          languageTranslationId: labelForm.languageTranslationId,
          translationJson,
          remark: labelForm.remark || '',
        })
      : await insertLanguageTranslation({
          companyId: Number(shop.companyId || 0),
          companyName: shop.companyName || '',
          language: labelForm.translationLanguage,
          type: 4,
          entityId: tagId,
          entityName: labelForm.labelName,
          translationJson,
          remark: labelForm.remark || '',
        });
    if (resp.code === 200) {
      message.success('标签翻译保存成功');
    } else {
      message.warning(resp.msg || '标签保存成功，翻译保存失败');
    }
  };

  const parseTranslationJson = (translationJson?: string) => {
    if (!translationJson) return undefined;
    try {
      return JSON.parse(translationJson) as { tagName?: string };
    } catch (error) {
      console.error('解析标签翻译失败:', error);
      return undefined;
    }
  };

  const loadTagTranslation = async (tagId?: number, shopId?: number) => {
    labelForm.translationTagName = '';
    labelForm.languageTranslationId = undefined;
    if (!tagId || !shopId) return;

    const shop = getSelectedShop(shopId);
    if (!shop) return;

    try {
      const resp = await fetchLanguageTranslationList({
        pageNum: 1,
        pageSize: 20,
        data: {
          companyId: Number(shop.companyId || 0),
          language: labelForm.translationLanguage,
          type: 4,
          status: 0,
          entityId: tagId,
        },
      });
      if (resp.code === 200) {
        const translation = resp.result.list?.[0];
        const translationData = parseTranslationJson(translation?.translationJson);
        labelForm.languageTranslationId = translation?.languageTranslationId;
        labelForm.translationTagName = translationData?.tagName || '';
      }
    } catch (error) {
      console.error('加载标签翻译失败:', error);
    }
  };

  const handleLabelTranslationLanguageChange = async () => {
    labelForm.translationTagName = '';
    labelForm.languageTranslationId = undefined;
    if (isEdit.value) {
      await loadTagTranslation(labelForm.shopFoodLabelId, labelForm.shopId);
    }
  };

  // 处理日期范围变化（开始时间传 00:00:00，结束时间传 23:59:59）
  const handleDateRangeChange = (dates: [Dayjs, Dayjs] | null) => {
    if (dates && dates[0] && dates[1]) {
      // 开始时间：当天 00:00:00
      queryParams.createtimeBegin = dates[0].startOf('day').format('YYYY-MM-DD HH:mm:ss');
      // 结束时间：当天 23:59:59
      queryParams.createtimeEnd = dates[1].endOf('day').format('YYYY-MM-DD HH:mm:ss');
    } else {
      queryParams.createtimeBegin = '';
      queryParams.createtimeEnd = '';
    }
  };

  // 加载店铺列表
  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 999,
        data: { shopStatus: 0 },
      });
      if (response.code === 200) {
        shopList.value = response.result.list || [];
        queryParams.data.shopId = restoreSelectedShop(shopList.value);
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
      message.error('加载店铺列表失败');
    }
  };

  // 加载标签列表
  const loadLabelList = async () => {
    if (!queryParams.data.shopId) {
      tableData.value = [];
      total.value = 0;
      message.warning('请选择餐厅');
      return;
    }
    try {
      loading.value = true;

      // 构建查询参数，过滤掉空值
      const params: any = {
        pageNum: queryParams.pageNum,
        pageSize: queryParams.pageSize,
        data: {},
      };

      // 添加时间范围
      if (queryParams.createtimeBegin) {
        params.createtimeBegin = queryParams.createtimeBegin;
      }
      if (queryParams.createtimeEnd) {
        params.createtimeEnd = queryParams.createtimeEnd;
      }

      // 添加数据筛选条件（只添加有值的字段）
      if (queryParams.data.shopId !== undefined && queryParams.data.shopId !== null) {
        params.data.shopId = queryParams.data.shopId;
      }
      if (queryParams.data.labelName) {
        params.data.tagName = queryParams.data.labelName;
      }
      if (queryParams.data.tagType !== undefined && queryParams.data.tagType !== null) {
        params.data.tagType = queryParams.data.tagType;
      }
      if (queryParams.data.labelStatus !== undefined && queryParams.data.labelStatus !== null) {
        params.data.tagStatus = queryParams.data.labelStatus;
      }

      const response = await fetchTagList(params);
      if (response.code === 200) {
        // 将 API 返回的字段映射为界面使用的字段
        const list = (response.result.list || []).map((item: any) => ({
          ...item,
          shopFoodLabelId: item.shopFoodTagId,
          labelName: item.tagName,
          tagType: item.tagType,
          labelStatus: item.tagStatus,
        }));
        tableData.value = list;
        total.value = response.result.total || 0;
      } else {
        message.error(response.msg || '查询失败');
        tableData.value = [];
        total.value = 0;
      }
    } catch (error) {
      console.error('加载标签列表失败:', error);
      message.error('加载标签列表失败，请稍后重试');
      tableData.value = [];
      total.value = 0;
    } finally {
      loading.value = false;
    }
  };

  // 查询
  const handleSearch = () => {
    queryParams.pageNum = 1;
    loadLabelList();
  };

  // 打开新增标签弹窗
  const openAddLabelModal = () => {
    isEdit.value = false;
    labelModalTitle.value = '新增标签';
    labelForm.shopFoodLabelId = undefined;
    labelForm.shopId = queryParams.data.shopId;
    labelForm.labelName = '';
    labelForm.tagType = undefined;
    labelForm.labelStatus = 0;
    labelForm.sort = 1;
    labelForm.remark = '';
    labelForm.translationLanguage = 'en';
    labelForm.languageTranslationId = undefined;
    labelForm.translationTagName = '';
    labelModalVisible.value = true;
  };

  // 打开编辑标签弹窗
  const handleEditLabel = async (record: any) => {
    isEdit.value = true;
    labelModalTitle.value = '编辑标签';
    labelModalLoading.value = true;
    try {
      const resp = await fetchTagDetail({
        shopFoodTagId: record.shopFoodLabelId,
      });
      if (resp.code === 200 && resp.result) {
        const data = resp.result;
        labelForm.shopFoodLabelId = data.shopFoodTagId;
        labelForm.shopId = data.shopId as number | undefined;
        labelForm.labelName = data.tagName || '';
        labelForm.tagType = data.tagType as TagType | undefined;
        labelForm.labelStatus =
          (data.tagStatus as 0 | 1 | undefined) !== undefined ? (data.tagStatus as 0 | 1) : 0;
        labelForm.sort = data.sort ?? 1;
        labelForm.remark = data.remark || '';
        labelForm.translationLanguage = 'en';
        labelForm.languageTranslationId = undefined;
        labelForm.translationTagName = '';
        await loadTagTranslation(data.shopFoodTagId, data.shopId);
        labelModalVisible.value = true;
      } else {
        message.error(resp.msg || '获取标签详情失败');
      }
    } catch (error) {
      console.error('获取标签详情异常:', error);
      message.error('获取标签详情异常，请稍后重试');
    } finally {
      labelModalLoading.value = false;
    }
  };

  // 保存标签（新增 / 编辑）
  const handleLabelModalOk = async () => {
    // 新增时必须选择店铺
    if (!isEdit.value && !labelForm.shopId) {
      message.warning('请选择店铺');
      return;
    }
    if (!labelForm.labelName) {
      message.warning('请输入标签名称');
      return;
    }
    if (!labelForm.tagType) {
      message.warning('请选择标签类型');
      return;
    }
    if (labelForm.sort < 0) {
      message.warning('排序值不能为负数');
      return;
    }

    labelModalLoading.value = true;
    try {
      if (isEdit.value) {
        // 编辑
        const payload = {
          shopFoodTagId: labelForm.shopFoodLabelId,
          tagName: labelForm.labelName,
          tagType: labelForm.tagType,
          sort: labelForm.sort,
          remark: labelForm.remark,
        };
        const resp = await updateTag(payload);
        if (resp.code === 200) {
          message.success('编辑标签成功');
          await submitTagTranslation(labelForm.shopFoodLabelId);
          labelModalVisible.value = false;
          await loadLabelList();
        } else {
          message.error(resp.msg || '编辑标签失败');
        }
      } else {
        // 新增
        const payload = {
          shopId: labelForm.shopId,
          tagName: labelForm.labelName,
          tagType: labelForm.tagType,
          tagStatus: labelForm.labelStatus ?? 0,
          sort: labelForm.sort,
          remark: labelForm.remark,
        };
        const resp = await addTag(payload);
        if (resp.code === 200) {
          message.success('新增标签成功');
          await submitTagTranslation(Number(resp.result || 0));
          labelModalVisible.value = false;
          await loadLabelList();
        } else {
          message.error(resp.msg || '新增标签失败');
        }
      }
    } catch (error) {
      console.error('保存标签异常:', error);
      message.error('保存标签异常，请稍后重试');
    } finally {
      labelModalLoading.value = false;
    }
  };

  // 取消标签弹窗
  const handleLabelModalCancel = () => {
    labelModalVisible.value = false;
    labelForm.translationLanguage = 'en';
    labelForm.languageTranslationId = undefined;
    labelForm.translationTagName = '';
  };

  // 禁用 / 启用 标签
  const handleToggleLabelStatus = async (record: any) => {
    const targetStatus = record.labelStatus === 0 ? 1 : 0;
    try {
      const resp = await forbidTag({
        shopFoodTagId: record.shopFoodLabelId,
        tagStatus: targetStatus,
      });
      if (resp.code === 200) {
        message.success(targetStatus === 1 ? '标签已禁用' : '标签已启用');
        await loadLabelList();
      } else {
        message.error(resp.msg || '更新标签状态失败');
      }
    } catch (error) {
      console.error('更新标签状态异常:', error);
      message.error('更新标签状态异常，请稍后重试');
    }
  };

  // 删除标签
  const handleDeleteLabel = async (record: any) => {
    try {
      const resp = await deleteTag({
        shopFoodTagId: record.shopFoodLabelId,
      });
      if (resp.code === 200) {
        message.success('删除标签成功');
        await loadLabelList();
      } else {
        message.error(resp.msg || '删除标签失败');
      }
    } catch (error) {
      console.error('删除标签异常:', error);
      message.error('删除标签异常，请稍后重试');
    }
  };

  // 查看标签详情
  const handleViewLabelDetail = async (record: any) => {
    labelDetailVisible.value = true;
    labelDetailLoading.value = true;
    labelDetail.value = null;
    try {
      const resp = await fetchTagDetail({
        shopFoodTagId: record.shopFoodLabelId,
      });
      if (resp.code === 200 && resp.result) {
        labelDetail.value = resp.result;
      } else {
        message.error(resp.msg || '获取标签详情失败');
      }
    } catch (error) {
      console.error('获取标签详情异常:', error);
      message.error('获取标签详情异常，请稍后重试');
    } finally {
      labelDetailLoading.value = false;
    }
  };

  // 重置
  const handleReset = () => {
    queryParams.pageNum = 1;
    queryParams.pageSize = 20;
    queryParams.createtimeBegin = '';
    queryParams.createtimeEnd = '';
    queryParams.data.labelName = '';
    queryParams.data.tagType = undefined;
    queryParams.data.labelStatus = undefined;
    dateRange.value = null;
    loadLabelList();
  };

  // 分页变化
  const handlePageChange = (page: number) => {
    queryParams.pageNum = page;
    loadLabelList();
  };

  // 每页条数变化
  const handlePageSizeChange = (current: number, size: number) => {
    queryParams.pageNum = 1;
    queryParams.pageSize = size;
    loadLabelList();
  };

  // 路由
  const router = useRouter();

  // 返回上一页
  const goBack = () => {
    router.back();
  };

  // 初始化
  onMounted(async () => {
    await loadShopList();
    await loadLabelList();
  });
</script>

<style scoped lang="less">
  .food-label-manage {
    padding: 24px;
    background: #f0f2f5;
    min-height: 100vh;

    .search-form {
      margin-bottom: 16px;
      padding: 16px;
      background: #fff;
      border-radius: 4px;

      :deep(.ant-form-item) {
        margin-bottom: 16px;
      }
    }

    .pagination-container {
      margin-top: 16px;
      text-align: right;
    }

    :deep(.ant-card) {
      .ant-card-head {
        border-bottom: 1px solid #f0f0f0;
      }

      .ant-card-body {
        padding: 24px;
      }
    }

    :deep(.ant-table) {
      .ant-table-thead > tr > th {
        background: #fafafa;
        font-weight: 600;
      }
    }
  }
</style>
